import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Globe, Plus, Trash2, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Select, Card, ConfirmModal } from "@/components/ui";
import { companiesApi } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";

interface CompanyFormData {
  name: string;
  document_type: "cpf" | "cnpj";
  document: string;
  email: string;
  phone: string;
  website: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_neighborhood: string;
  address_city: string;
  address_state: string;
  address_zip: string;
}

const BRAZILIAN_STATES = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const { company, updateCompany, user } = useAuthStore();

  const [newDomain, setNewDomain] = useState("");
  const [deleteDomainId, setDeleteDomainId] = useState<string | null>(null);

  // Carregar empresa se ainda não foi carregada
  useEffect(() => {
    if (!company && user?.company_id) {
      companiesApi
        .get(user.company_id)
        .then((response) => {
          if (response.success && response.data) {
            updateCompany(response.data);
          }
        })
        .catch((error) => {
          console.error("Erro ao carregar empresa:", error);
        });
    }
  }, [company, user?.company_id, updateCompany]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CompanyFormData>({
    defaultValues: {
      name: "",
      document_type: "cnpj",
      document: "",
      email: "",
      phone: "",
      website: "",
      address_street: "",
      address_number: "",
      address_complement: "",
      address_neighborhood: "",
      address_city: "",
      address_state: "",
      address_zip: "",
    },
  });

  const { data: companyData, isLoading } = useQuery({
    queryKey: ["company", company?.id],
    queryFn: () => companiesApi.get(company!.id),
    enabled: !!company?.id,
  });

  useEffect(() => {
    if (companyData?.data) {
      const c = companyData.data;
      reset({
        name: c.name || "",
        document_type: c.document_type || "cnpj",
        document: c.document || "",
        email: c.email || "",
        phone: c.phone || "",
        website: c.website || "",
        address_street: c.address_street || "",
        address_number: c.address_number || "",
        address_complement: c.address_complement || "",
        address_neighborhood: c.address_neighborhood || "",
        address_city: c.address_city || "",
        address_state: c.address_state || "",
        address_zip: c.address_zip || "",
      });
    }
  }, [companyData, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: CompanyFormData) =>
      companiesApi.update(company!.id, data),
    onSuccess: () => {
      toast.success("Empresa atualizada com sucesso");
      queryClient.invalidateQueries({ queryKey: ["company", company?.id] });
      if (companyData?.data) {
        updateCompany(companyData.data);
      }
    },
    onError: (error: any) => {
      console.error("Erro na atualização:", error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Erro ao atualizar empresa";
      toast.error(errorMessage);
    },
  });

  const addDomainMutation = useMutation({
    mutationFn: (domain: string) =>
      companiesApi.addDomain(company!.id, { domain }),
    onSuccess: () => {
      toast.success("Domínio adicionado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["company", company?.id] });
      setNewDomain("");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Erro ao adicionar domínio",
      );
    },
  });

  const removeDomainMutation = useMutation({
    mutationFn: (domainId: string) =>
      companiesApi.removeDomain(company!.id, domainId),
    onSuccess: () => {
      toast.success("Domínio removido com sucesso");
      queryClient.invalidateQueries({ queryKey: ["company", company?.id] });
      setDeleteDomainId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Erro ao remover domínio");
    },
  });

  const onSubmit = (data: CompanyFormData) => {
    updateMutation.mutate(data);
  };

  const handleAddDomain = () => {
    if (newDomain.trim()) {
      addDomainMutation.mutate(newDomain.trim());
    }
  };

  const domains = companyData?.data?.domains || [];

  if (!company || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">
          Configurações
        </h1>
        <p className="text-text-secondary mt-1 text-sm sm:text-base">
          Gerencie as configurações da sua empresa
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        {/* Company Info */}
        <Card className="p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Informações da Empresa
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Nome da Empresa *"
              format="name"
              {...register("name", { required: "Nome é obrigatório" })}
              error={errors.name?.message}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Select
                label="Tipo"
                {...register("document_type")}
                options={[
                  { value: "cnpj", label: "CNPJ" },
                  { value: "cpf", label: "CPF" },
                ]}
              />
              <div className="col-span-2">
                <Input
                  label="Documento"
                  format="document"
                  {...register("document")}
                  placeholder="00.000.000/0001-00"
                  hint="Formatação automática"
                />
              </div>
            </div>
            <Input
              label="Email *"
              type="email"
              format="email"
              {...register("email", {
                required: "Email é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email inválido",
                },
              })}
              error={errors.email?.message}
            />
            <Input
              label="Telefone"
              format="phone"
              {...register("phone")}
              placeholder="(00) 00000-0000"
              hint="Formatação automática"
            />
            <div className="sm:col-span-2">
              <Input
                label="Website"
                {...register("website")}
                placeholder="https://suaempresa.com.br"
              />
            </div>
          </div>
        </Card>

        {/* Address */}
        <Card className="p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-4">
            Endereço
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Input
              label="CEP"
              format="cep"
              {...register("address_zip")}
              placeholder="00000-000"
              hint="Formatação automática"
            />
            <Input
              label="Rua"
              format="name"
              {...register("address_street")}
              className="sm:col-span-2"
            />
            <Input label="Número" {...register("address_number")} />
            <Input
              label="Complemento"
              format="name"
              {...register("address_complement")}
            />
            <Input
              label="Bairro"
              format="name"
              {...register("address_neighborhood")}
            />
            <Input label="Cidade" format="name" {...register("address_city")} />
            <Select
              label="Estado"
              {...register("address_state")}
              options={[
                { value: "", label: "Selecione..." },
                ...BRAZILIAN_STATES.map((state) => ({
                  value: state,
                  label: state,
                })),
              ]}
            />
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-center sm:justify-end">
          <Button
            type="submit"
            isLoading={updateMutation.isPending}
            disabled={!isDirty}
            className="w-full sm:w-auto"
          >
            <Save className="w-4 h-4 mr-2" />
            Salvar Alterações
          </Button>
        </div>
      </form>

      {/* Domains */}
      <Card className="p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary" />
          Domínios
        </h2>
        <p className="text-text-muted mb-4">
          Configure os domínios que podem acessar o sistema da sua empresa.
        </p>

        {/* Domain List */}
        <div className="space-y-2 mb-4">
          {domains.length === 0 ? (
            <p className="text-text-muted py-4 text-center">
              Nenhum domínio configurado
            </p>
          ) : (
            domains.map((domain: any) => (
              <div
                key={domain.id}
                className="flex items-center justify-between p-3 bg-dark-lighter rounded-lg border border-white/5"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-text-muted" />
                  <span className="font-mono text-text-primary">
                    {domain.domain}
                  </span>
                  {domain.is_primary && (
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                      Principal
                    </span>
                  )}
                </div>
                {!domain.is_primary && (
                  <button
                    type="button"
                    onClick={() => setDeleteDomainId(domain.id)}
                    className="p-2 text-text-muted hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Add Domain */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="novo-dominio.com.br"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            className="flex-1"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddDomain}
            isLoading={addDomainMutation.isPending}
            disabled={!newDomain.trim()}
            className="w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar
          </Button>
        </div>
      </Card>

      {/* Delete Domain Confirmation */}
      <ConfirmModal
        isOpen={!!deleteDomainId}
        onClose={() => setDeleteDomainId(null)}
        onConfirm={() =>
          deleteDomainId && removeDomainMutation.mutate(deleteDomainId)
        }
        title="Remover Domínio"
        message="Tem certeza que deseja remover este domínio? Usuários que acessam por este domínio não poderão mais entrar no sistema."
        confirmText="Remover"
        variant="danger"
        isLoading={removeDomainMutation.isPending}
      />
    </div>
  );
}
